import styled, { CSSProperties } from 'styled-components';

import { BodilColor, NorlysColor, NorlysFont } from '~src/branding';
import { boxShadows } from '~src/branding/boxShadow';
import { useDisplayContext } from '~src/context/DisplayContext';

import { ButtonNorlys } from '../Button';
import { DEFAULT_HEATING_OPTIONS } from './common';
import { DualDropdownOption, Props } from './types';

const { default: defaultColor } = BodilColor.base;

const Wrapper = styled.div({ padding: '4px' });

const StyledContainer = styled.div<{ dir?: CSSProperties['flexDirection'] }>(({ dir = 'row' }) => ({
  backgroundColor: 'transparent',
  display: 'flex',
  flexDirection: dir,
  alignItems: 'center',
}));

const OptionBox = styled.div<{ height?: CSSProperties['height'] }>(({ height = '300px' }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-evenly',
  width: '250px',
  height,
  padding: 0,
  margin: '16px auto',
  ...boxShadows.norlys,
}));

const BoxLabel = styled.div({
  textAlign: 'center',
  ...NorlysFont.style('heading', 20),
  fontWeight: 200,
  marginTop: '16px',
});

const IconBox = styled.div({
  height: '55px',
  marginTop: '12px',
  display: 'flex',
  alignItems: 'center',
});

export const HeatingTypeSelectNorlys = ({
  options = DEFAULT_HEATING_OPTIONS,
  onChange,
  label,
  value,
  style,
}: Props) => {
  const { isMobile } = useDisplayContext();

  const labelStyle = {
    ...(isMobile ? NorlysFont.style('default', 16) : NorlysFont.style('heading', 30)),
    fontWeight: 400,
    marginBottom: '4px',
    textAlign: 'center',
  } as CSSProperties;

  return (
    <Wrapper style={style}>
      {label && <div style={labelStyle}>{label}</div>}
      <StyledContainer {...{ dir: isMobile ? 'column' : 'row' }}>
        {options.map(option => (
          <Option key={`heating-option-${option.value}`} {...{ option, selected: value === option.value, onChange }} />
        ))}
      </StyledContainer>
    </Wrapper>
  );
};

type OptionProps = {
  selected?: boolean;
  option: DualDropdownOption;
  onChange: (value: string) => void;
};

const Option = ({ option, selected, onChange: onChangeInput }: OptionProps) => {
  const { isMobile } = useDisplayContext();
  const { label, value, Icon, SecondaryIcon } = option;

  const iconSize = SecondaryIcon ? 28 : 42;
  const iconProps = { color: defaultColor, width: iconSize, height: iconSize };

  const buttonStyle: CSSProperties = {
    width: '102px',
    backgroundColor: selected ? NorlysColor.primary.acid : NorlysColor.secondary.darkGreen,
    color: 'white',
  };

  const onChange = selected ? () => {} : () => onChangeInput(value);

  return (
    <OptionBox
      {...{
        ...(isMobile && { height: '200px' }),
        style: { cursor: !selected ? 'pointer' : 'default' },
        key: value,
        onClick: onChange,
      }}
    >
      <BoxLabel>{label}</BoxLabel>
      <IconBox>
        {Icon && <Icon {...iconProps} />}
        {SecondaryIcon && <SecondaryIcon {...iconProps} />}
      </IconBox>
      <ButtonNorlys disabled={selected} style={buttonStyle} onClick={onChange}>
        {selected ? 'Valgt' : 'Vælg'}
      </ButtonNorlys>
    </OptionBox>
  );
};
